import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";

// Client functions
import { updateReadTranscript } from "sj-ui-toolkit/src/api";
import { getLessonTranscriptUrl } from "sj-ui-toolkit/src/client";

const TranscriptShadow = ({ html }) => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadow.innerHTML = html;
  }, [html]);

  return (
    <div
      ref={hostRef}
      style={{
        containerType: "inline-size",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    />
  );
};

const TextViewer = ({ lessonId, readTranscript }) => {
  const [transcriptHtml, setTranscriptHtml] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTranscript = async () => {
      setLoading(true);
      try {
        const url = await getLessonTranscriptUrl({ lessonId });
        const response = await fetch(url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const styles = Array.from(doc.querySelectorAll("style"))
          .map(el =>
            el.outerHTML.replace(
              /@media\s*(\([^)]*(?:max-width|min-width)[^)]*\))/g,
              "@container $1"
            )
          )
          .join("");
        const sanitized = styles + DOMPurify.sanitize(doc.body.innerHTML);
        setTranscriptHtml(sanitized);
      } catch (error) {
        console.error("Error fetching transcript:", error);
      }
      setLoading(false);
    };

    if (lessonId && !transcriptHtml) {
      fetchTranscript();
    }
  }, [lessonId]);

  useEffect(() => {
    const handleMessage = async event => {
      if (!readTranscript && event.data === "reachedEnd") {
        const result = await updateReadTranscript({ lessonId });
        if (!result) {
          console.error("Failed to send read transcript event");
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [lessonId]);

  if (loading) return <div>Loading transcript...</div>;

  return <TranscriptShadow html={transcriptHtml ?? ""} />;
};

export default TextViewer;
